'use client';

import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import get from 'lodash/get';
import { BASE_FILE_URL } from '@/common/constants';

const CHUNK_SIZE = 5;
const ALL_TAG = 'all';

// ---------------------------------------------------------------------------
// Hoạ tiết thổ cẩm — hoạ tiết hình học lấy cảm hứng từ nghề dệt của đồng bào
// Dao, Lô Lô ở Bằng Lang. Đây là điểm nhấn xuyên suốt trang: dùng làm đường
// phân cách và điểm nhấn góc thẻ, không dùng để trang trí tuỳ tiện.
// ---------------------------------------------------------------------------
const WovenDivider = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 240 16" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <pattern id="brocade" width="24" height="16" patternUnits="userSpaceOnUse">
      <path d="M0 8 L6 2 L12 8 L6 14 Z" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M12 8 L18 2 L24 8 L18 14 Z" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="6" cy="8" r="1.1" fill="currentColor" />
      <circle cx="18" cy="8" r="1.1" fill="currentColor" />
    </pattern>
    <rect width="240" height="16" fill="url(#brocade)" />
  </svg>
);

function useRevealOnScroll() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

const DestinationCard = ({ item, isLarge, websiteCode }) => {
  const href = websiteCode ? `/sub/${websiteCode}/${item?.menu?.slug || 'kham-pha'}/${item.slug}` : `/${item?.menu?.slug || 'kham-pha'}/${item.slug}`;

  return (
    <Link
      href={href}
      className={`group relative block overflow-hidden rounded-xl bg-forest-800 ${
        isLarge ? 'col-span-2 row-span-2 aspect-square md:aspect-auto' : 'aspect-[4/3]'
      }`}>
      <Image
        src={`${BASE_FILE_URL}/${item.avatarUrl}`}
        alt={item.title || item.name || ''}
        fill
        sizes={isLarge ? '(min-width: 768px) 50vw, 100vw' : '(min-width: 768px) 25vw, 50vw'}
        className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        quality={85}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/0 transition-opacity duration-300 group-hover:from-black/95" />

      {/* Điểm nhấn hoạ tiết ở góc, chỉ hiện khi hover — dùng lại mô-típ của WovenDivider */}
      <WovenDivider className="pointer-events-none absolute -right-2 top-3 h-4 w-16 rotate-90 text-brass-400 opacity-0 transition-opacity duration-300 group-hover:opacity-70" />

      <div className="absolute inset-x-0 bottom-0 flex flex-col p-4 md:p-5">
        {item.tag && (
          <span className="mb-2 inline-flex w-fit items-center rounded-full border border-brass-400/40 bg-forest-950/60 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-brass-300">
            {item.tag}
          </span>
        )}
        <h3 className={`font-display font-semibold leading-snug text-cream-100 ${isLarge ? 'text-xl md:text-[26px]' : 'text-[14px] md:text-[15px]'}`}>
          {item.title || item.name}
        </h3>

        {item.location && <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-cream-100/50">{item.location}</p>}

        {isLarge && item.summary && <p className="mt-2 max-w-md text-sm text-cream-100/75 line-clamp-2 md:line-clamp-3">{item.summary}</p>}

        <span
          className={`mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brass-300 opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100 ${
            isLarge ? 'md:opacity-100' : ''
          }`}>
          Xem chi tiết
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </Link>
  );
};

const DestinationCluster = ({ items, websiteCode }) => {
  const { ref, visible } = useRevealOnScroll();

  return (
    <div
      ref={ref}
      className={`grid grid-cols-2 gap-3 transition-all duration-700 ease-out md:grid-cols-4 md:gap-4 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}>
      {items.map((item, index) => (
        <DestinationCard key={item.id || item.slug} item={item} isLarge={index === 0} websiteCode={websiteCode} />
      ))}
    </div>
  );
};

const TourismExplorePage = ({ tourismData, websiteCode }) => {
  // Lấy dữ liệu đúng theo cách bản component gốc dùng: an toàn với payload
  // thiếu field, trả về mảng rỗng khi không có `results`.
  const initialItems = tourismData;
  const total = get(tourismData, 'total', initialItems.length);

  const [items, setItems] = useState(initialItems);
  const [activeTag, setActiveTag] = useState(ALL_TAG);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(initialItems.length < total);

  const tags = useMemo(() => {
    const unique = Array.from(new Set(initialItems.map((item) => item.tag).filter(Boolean)));
    return [ALL_TAG, ...unique];
  }, [initialItems]);

  const filteredItems = useMemo(() => (activeTag === ALL_TAG ? items : items.filter((item) => item.tag === activeTag)), [items, activeTag]);

  const clusters = useMemo(() => {
    const groups = [];
    for (let i = 0; i < filteredItems.length; i += CHUNK_SIZE) {
      groups.push(filteredItems.slice(i, i + CHUNK_SIZE));
    }
    return groups;
  }, [filteredItems]);

  const handleFilterChange = useCallback((tag) => {
    setActiveTag(tag);
  }, []);

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await getTourismData({ tag: activeTag === ALL_TAG ? undefined : activeTag, page: nextPage });
      setItems((prev) => [...prev, ...res.results]);
      setPage(nextPage);
      setHasMore((prev) => prev && res.results.length > 0 && items.length + res.results.length < res.total);
    } finally {
      setLoadingMore(false);
    }
  }, [page, activeTag, items.length]);

  if (!initialItems.length) return null;

  const heroImage = initialItems[0]?.avatarUrl;

  return (
    <main className="bg-forest-950 text-cream-100">
      {/* ------------------------------------------------------------- Hero */}
      <section className="relative flex min-h-[68vh] items-end overflow-hidden md:min-h-[78vh]">
        {heroImage && <Image src={`${BASE_FILE_URL}/${heroImage}`} alt="Bằng Lang" fill priority className="object-cover" quality={85} />}
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/70 to-forest-950/20" />
        <div className="absolute inset-0 bg-forest-950/30" />

        <div className="relative z-10 mx-auto w-full max-w-screen-xl px-4 pb-14 pt-24 md:px-8 md:pb-20">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-brass-300">Miền cao nguyên đá</p>
          <h1 className="font-display max-w-3xl text-4xl font-semibold leading-[1.08] md:text-6xl">Khám phá Bằng Lang</h1>
          <p className="mt-4 max-w-xl text-sm text-cream-100/70 md:text-base">
            Nơi đá tai mèo, ruộng bậc thang và sắc thổ cẩm của người Dao, Lô Lô cùng viết nên một vùng đất giữ nguyên vẻ hoang sơ.
          </p>
          <WovenDivider className="mt-8 h-4 w-40 text-brass-400/80" />
        </div>
      </section>

      {/* ---------------------------------------------------------- Filters */}
      <div className="sticky top-0 z-20 border-b border-cream-100/10 bg-forest-950/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-screen-xl gap-2 overflow-x-auto px-4 py-3 [scrollbar-width:none] md:px-8 [&::-webkit-scrollbar]:hidden">
          {tags.map((tag) => (
            <button
              key={tag}
              onClick={() => handleFilterChange(tag)}
              className={`flex-shrink-0 rounded-full border px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-colors duration-200 ${
                activeTag === tag
                  ? 'border-brass-400 bg-brass-400 text-forest-950'
                  : 'border-cream-100/20 text-cream-100/70 hover:border-brass-400/50 hover:text-cream-100'
              }`}>
              {tag === ALL_TAG ? 'Tất cả' : tag}
            </button>
          ))}
        </div>
      </div>

      {/* ------------------------------------------------------------- Grid */}
      <section className="mx-auto max-w-screen-xl px-4 py-10 md:px-8 md:py-14">
        {clusters.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-20 text-center text-cream-100/60">
            <p>Chưa có điểm đến nào cho bộ lọc này.</p>
            <button onClick={() => handleFilterChange(ALL_TAG)} className="text-sm text-brass-300 underline">
              Xem tất cả điểm đến
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:gap-5">
            {clusters.map((clusterItems, idx) => (
              <DestinationCluster key={idx} items={clusterItems} websiteCode={websiteCode} />
            ))}
          </div>
        )}

        {hasMore && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="rounded-full border border-brass-400/50 px-6 py-2.5 text-sm font-medium text-brass-300 transition-colors duration-200 hover:bg-brass-400 hover:text-forest-950 disabled:opacity-50">
              {loadingMore ? 'Đang tải...' : 'Xem thêm điểm đến'}
            </button>
          </div>
        )}
      </section>

      {/* -------------------------------------------------------------- CTA */}
      <section className="relative overflow-hidden border-t border-cream-100/10 py-16 text-center">
        <WovenDivider className="mx-auto mb-6 h-4 w-40 text-brass-400/60" />
        <h2 className="font-display mx-auto max-w-xl text-2xl font-semibold md:text-3xl">Bằng Lang đang chờ những bước chân tiếp theo</h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-cream-100/60">
          Lên lịch trình, chọn mùa lễ hội hoặc mùa nước đổ phù hợp với chuyến đi của bạn.
        </p>
      </section>
    </main>
  );
};

export default TourismExplorePage;
